// components/PWAInstallPrompt.jsx
import React, { useState, useEffect } from 'react';
import { FaDownload, FaTimes, FaAndroid, FaApple, FaShare } from 'react-icons/fa';
import './PWAInstallPrompt.css';

const PWAInstallPrompt = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstalled, setIsInstalled] = useState(false);
    const [platform, setPlatform] = useState('');

    useEffect(() => {
        // Detect platform
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        if (/android/i.test(userAgent)) {
            setPlatform('android');
        } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
            setPlatform('ios');
        } else {
            setPlatform('web');
        }

        // Check if app is already installed
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsInstalled(true);
            setShowPrompt(false);
            return;
        }

        // Check if user dismissed the prompt before
        const dismissed = localStorage.getItem('pwa_prompt_dismissed');
        if (dismissed === 'true') {
            setShowPrompt(false);
            return;
        }

        // Listen for the beforeinstallprompt event
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShowPrompt(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Listen for app installed event
        const handleAppInstalled = () => {
            setIsInstalled(true);
            setShowPrompt(false);
            localStorage.setItem('pwa_prompt_dismissed', 'true');
        };

        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const result = await deferredPrompt.userChoice;
            if (result.outcome === 'accepted') {
                setIsInstalled(true);
                setShowPrompt(false);
                localStorage.setItem('pwa_prompt_dismissed', 'true');
            }
            setDeferredPrompt(null);
        }
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        localStorage.setItem('pwa_prompt_dismissed', 'true');
    };

    const handleLater = () => {
        setShowPrompt(false);
        setTimeout(() => {
            const dismissed = localStorage.getItem('pwa_prompt_dismissed');
            if (dismissed !== 'true') {
                setShowPrompt(true);
            }
        }, 7 * 24 * 60 * 60 * 1000); // Show again after 7 days
    };

    if (!showPrompt || isInstalled) return null;

    return (
        <div className="pwa-install-prompt">
            <div className="pwa-install-content">
                <button className="pwa-close-btn" onClick={handleDismiss}>
                    <FaTimes />
                </button>
                
                <div className="pwa-install-header">
                    <div className="pwa-app-icon">
                        <span>🏢</span>
                    </div>
                    <div className="pwa-app-info">
                        <h3>Franmax India</h3>
                        <p>Franchise Portal</p>
                    </div>
                </div>

                <div className="pwa-install-body">
                    <p className="pwa-install-message">
                        Install this app on your phone for quick access anytime!
                    </p>
                    
                    {platform === 'android' && (
                        <div className="pwa-install-steps">
                            <div className="step">
                                <span className="step-icon">1</span>
                                <span>Tap <strong>Add to Home Screen</strong></span>
                            </div>
                            <div className="step">
                                <span className="step-icon">2</span>
                                <span>Tap <strong>Install</strong></span>
                            </div>
                        </div>
                    )}
                    
                    {platform === 'ios' && (
                        <div className="pwa-install-steps">
                            <div className="step">
                                <span className="step-icon">1</span>
                                <span>Tap <FaShare className="step-icon-share" /> <strong>Share</strong></span>
                            </div>
                            <div className="step">
                                <span className="step-icon">2</span>
                                <span>Scroll down and tap <strong>Add to Home Screen</strong></span>
                            </div>
                            <div className="step">
                                <span className="step-icon">3</span>
                                <span>Tap <strong>Add</strong></span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="pwa-install-footer">
                    <button className="pwa-install-btn" onClick={handleInstall}>
                        <FaDownload />
                        Install App
                    </button>
                    <button className="pwa-later-btn" onClick={handleLater}>
                        Remind Later
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PWAInstallPrompt;